/* -----------------Globals--------------- */

/* -----------------UI--------------- */
import { Platform } from "react-native";
import { Appbar, Text } from "react-native-paper";

import AppbarAction from "@memorang/ui/src/components/AppbarAction";
import AvatarGenerator from "@memorang/ui/src/components/AvatarGenerator/AvatarGenerator";
import Box from "@memorang/ui/src/components/Box";
import { CustomBreadcrumb } from "@memorang/ui/src/components/CustomBreadcrumb";
import type { BreadcrumbType } from "@memorang/ui/src/components/CustomBreadcrumbWeb";
import RowStats, { type Stat } from "@memorang/ui/src/components/RowStats";
import TooltipWrapper from "@memorang/ui/src/components/TooltipWrapper";
/* -----------------Helpers & Hooks--------------- */
import { useAppTheme } from "@memorang/ui/src/useAppTheme";
import { useDevice } from "@memorang/ui/src/useDevice";

import CommandbarContainer from "./CommandbarContainer";
import type { CustomNavigationBarProps } from "./types";
const TitleContent = ({
	headerTitle,
	subtitle,
	stats,
	breadcrumbs,
	renderSearchbar,
}: {
	align?: "center" | "flex-start";
	headerTitle?: string;
	subtitle?: string | React.ReactNode;
	stats?: Stat[];
	breadcrumbs?: BreadcrumbType[];
	renderSearchbar?: () => React.ReactNode;
}) => {
	const isWeb = Platform.OS === "web";
	const { isMobile, isMediumWindowSize } = useDevice();
	return (
		<Box gap={isMobile || isMediumWindowSize ? 8 : 16} width={"100%"}>
			{renderSearchbar?.()}
			<Box gap={4} paddingTop={isMobile ? 0 : 16}>
				{breadcrumbs?.length && !isMobile && isWeb ? (
					<CustomBreadcrumb breadcrumbs={breadcrumbs} size="small" />
				) : null}
				<Box>
					{headerTitle && (
						<Text
							numberOfLines={1}
							style={{ maxWidth: isMobile ? 400 : "100%" }}
							variant={isMobile ? "titleLarge" : "headlineMedium"}
						>
							{headerTitle as string}
						</Text>
					)}
					{subtitle && <Text variant="bodyMedium">{subtitle}</Text>}
				</Box>
			</Box>
			{stats?.length && !isMobile ? (
				<RowStats stacked={false} stats={stats} alignSelf={"flex-start"} />
			) : null}
		</Box>
	);
};

const CustomNavigationBar = (props: CustomNavigationBarProps) => {
	const {
		back,
		stats,
		options: { headerTitle, subtitle, headerRight, headerLeft },
		breadcrumbs,
		renderSearchbar,
		showHambugerMenu,
		align,
		showCommandBar,
		showSettings = true,
		showUserMenu = true,
		handleFeedbackNavigation,
		onAvatarPress,
		onBackPress,
		onHamburgerMenuPress,
		newUserInfo,
		toggleShowFeedbackDialog,
		trackCustomEvent,
		toggleCommandbar,
		handleTrackEvent,
		...rest
	} = props;
	const theme = useAppTheme();

	const { isMobile, isMediumWindowSize } = useDevice();

	// FOR SOME REASON, the medium prop medium does not work well on tablet
	const mode = isMobile ? "small" : isMediumWindowSize ? "large" : "medium";
	const finalAlign =
		align || (isMobile ? ("center" as const) : ("flex-start" as const));
	const renderHeaderRight = () => {
		if (headerRight) {
			return headerRight();
		}

		return (
			<Box
				flexDirection="row"
				paddingRight={12}
				alignItems="center"
				accessibilityRole="header"
			>
				{isWeb ? (
					<TooltipWrapper title="We value your feedback. Click here to share your thoughts.">
						<AppbarAction
							accessibilityRole="button"
							icon="comment-quote-outline"
							onPress={() =>
								handleFeedbackNavigation?.(toggleShowFeedbackDialog!)
							}
						/>
					</TooltipWrapper>
				) : (
					<AppbarAction
						accessibilityRole="button"
						icon="comment-quote-outline"
						onPress={() =>
							handleFeedbackNavigation?.(toggleShowFeedbackDialog!)
						}
					/>
				)}
				{/* {showSettings && (
					<Appbar.Action
						icon="cog-outline"
						style={{ marginRight: 16 }}
						onPress={() => router.push("/(protected)/account/settings")}
					/>
				)} */}
				{showUserMenu && (
					<Appbar.Action
						icon={() => (
							<AvatarGenerator
								avatarType="initials"
								size={24}
								firstName={newUserInfo?.firstName || newUserInfo?.name}
								lastName={newUserInfo?.lastName}
								customStyle={{
									position: "relative",
									top: undefined,
								}}
							/>
						)}
						onPress={() => onAvatarPress?.()}
					/>
				)}
			</Box>
		);
	};
	const isWeb = Platform.OS === "web";
	return (
		//@ts-ignore
		<Appbar.Header
			theme={theme}
			mode={mode}
			elevated
			accessibilityRole="header"
			{...rest}
			{...(isWeb
				? {
						style: {
							height: "fit-content",
						},
					}
				: {})}
		>
			{isWeb && (
				<Box
					position="absolute"
					alignItems="center"
					left={0}
					flexDirection="row"
					accessibilityRole="header"
				>
					{isMobile && showHambugerMenu && (
						<AppbarAction
							accessibilityRole="button"
							icon="menu"
							onPress={onHamburgerMenuPress}
						/>
					)}
					{showCommandBar && !isMobile && handleTrackEvent && (
						<Box paddingLeft={12}>
							<CommandbarContainer handleTrackEvent={handleTrackEvent} />
						</Box>
					)}
				</Box>
			)}

			{headerLeft?.()}
			{back && !headerLeft && (
				<Appbar.BackAction
					accessibilityLabel="Go back"
					accessibilityRole="button"
					onPress={onBackPress}
				/>
			)}
			<Appbar.Content
				title={
					<TitleContent
						align={finalAlign}
						headerTitle={headerTitle}
						subtitle={subtitle}
						stats={stats}
						breadcrumbs={breadcrumbs}
						renderSearchbar={renderSearchbar}
					/>
				}
			/>
			{renderHeaderRight()}
		</Appbar.Header>
	);
};

export default CustomNavigationBar;
